import { useTranslation } from 'react-i18next';
import { formatAmount } from '@shared/utils/formatAmount';
import { gratuityLabel } from '@features/booking/utils/gratuity';

interface FeeBreakdown {
  hasBreakdown: boolean;
  baseFee: number;
  gratuity: number;
  gratuityPercent: number;
  menuTax: number;
  salesTax: number;
  salesTaxPercent: number;
  serviceFee: number;
  serviceFeePercent: number;
  pairingsFee: number;
  total: number;
}

interface FeeBreakdownDisplayProps {
  feeBreakdown: FeeBreakdown;
  displayCurrency: string;
  /** USD a card issued outside Argentina is charged instead; shown as a note under the local total. */
  foreignCardUsd?: number | null;
  /** For a visitor from abroad: show only this USD total, not the local amounts. */
  usdOnlyTotal?: number | null;
}

export function FeeBreakdownDisplay({ feeBreakdown, displayCurrency, foreignCardUsd = null, usdOnlyTotal = null }: FeeBreakdownDisplayProps) {
  const { t, i18n } = useTranslation('booking');
  const money = (value: number) => formatAmount(value, i18n.language);
  const feeAmount = feeBreakdown.total;

  if (usdOnlyTotal != null && usdOnlyTotal > 0) {
    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center border-b border-border pb-3 tabular-nums" data-testid="usd-only-total">
          <p className="text-lg font-medium">{t('NEW_MODAL_TOTAL')}</p>
          <p className="text-lg font-medium">USD {money(usdOnlyTotal)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {feeBreakdown.hasBreakdown && (
        <>
          <div className="flex justify-between items-center text-sm text-foreground/70">
            <p>{t('NEW_MODAL_SUBTOTAL')}</p>
            <p>{displayCurrency} {money(feeBreakdown.baseFee)}</p>
          </div>
          {feeBreakdown.gratuity > 0 && (
            <div className="flex justify-between items-center text-sm text-foreground/70 tabular-nums">
              <p>{gratuityLabel(t('NEW_MODAL_SERVICE_GRATUITY'), feeBreakdown.gratuityPercent)}</p>
              <p>{displayCurrency} {money(feeBreakdown.gratuity)}</p>
            </div>
          )}
          {feeBreakdown.menuTax > 0 && (
            <div className="flex justify-between items-center text-sm text-foreground/70 tabular-nums">
              <p>{t('NEW_MODAL_TAX')}</p>
              <p>{displayCurrency} {money(feeBreakdown.menuTax)}</p>
            </div>
          )}
          {feeBreakdown.salesTax > 0 && (
            <div className="flex justify-between items-center text-sm text-foreground/70 tabular-nums">
              <p>{t('PAYMENT_SALES_TAX', { percent: feeBreakdown.salesTaxPercent })}</p>
              <p>{displayCurrency} {money(feeBreakdown.salesTax)}</p>
            </div>
          )}
          {feeBreakdown.serviceFee > 0 && (
            <div className="flex justify-between items-center text-sm text-foreground/70 tabular-nums">
              <p>{t('SERVICE_FEE', { percent: feeBreakdown.serviceFeePercent })}</p>
              <p>{displayCurrency} {money(feeBreakdown.serviceFee)}</p>
            </div>
          )}
          {feeBreakdown.pairingsFee > 0 && (
            <div className="flex justify-between items-center text-sm text-foreground/70 tabular-nums">
              <p>{t('PAYMENT_PAIRINGS_FEE')}</p>
              <p>{displayCurrency} {money(feeBreakdown.pairingsFee)}</p>
            </div>
          )}
        </>
      )}
      <div className="flex justify-between items-center border-b border-border pb-3 tabular-nums">
        <p className="text-lg font-medium">{t('NEW_MODAL_TOTAL')}</p>
        <p className="text-lg font-medium">
          {displayCurrency} {money(feeAmount)}
        </p>
      </div>
      {foreignCardUsd != null && foreignCardUsd > 0 && (
        <p className="text-sm text-foreground/70 tabular-nums" data-testid="foreign-card-usd">
          {t('PAYMENT_FOREIGN_CARD_USD', { amount: `USD ${money(foreignCardUsd)}` })}
        </p>
      )}
    </div>
  );
}
